// =============================================================================
// app/src/lib/fluxo.ts — o preparo do gráfico de fluxo de caixa.
//
// Funções PURAS (testáveis em node): agregação semanal, escala do desenho e o
// resumo da janela. O componente GraficoFluxo só desenha o que sai daqui.
//
// POR QUE AGREGAR EM SEMANAS: a janela de 90 dias daria ~120 barras — no
// celular cada uma teria 2 px e o gráfico viraria ruído. Acima de ~45 pontos,
// o dia vira semana (segunda como âncora) e o desenho fica com ≤ 18 barras
// legíveis. A janela curta (15 dias) continua diária.
// =============================================================================
import type { FluxoDia } from '@core/types';

/**
 * Acima disto, o gráfico agrega por semana.
 *
 * É 46, e o número tem motivo: `generate_series` é INCLUSIVA nas duas pontas,
 * então a janela curta da tela (`passado: 30, futuro: 15`) devolve 30+15+**1**
 * = 46 linhas — o mesmo +1 que a varredura do banco já afirma em
 * `varredura_funcional.sql` ("zero-fill completo: passado + futuro + hoje",
 * esperado 121 para `fluxo_de_caixa(30, 90)`).
 *
 * Com o teto em 45 a janela de 15 dias caía do lado errado da comparação e
 * vinha AGREGADA POR SEMANA — sete barras onde o cabeçalho deste arquivo
 * prometia 46 dias, e o painel de detalhe chamando "Dia" o que era semana. O
 * teste que dava isso por provado alimentava 15 linhas, nunca as 46 que a tela
 * de fato pede: o furo estava exatamente na fronteira que ninguém testou.
 * Achado em 27/09/2026, ao acrescentar o modo de 12 meses.
 */
export const TETO_DIARIO = 46;

/**
 * O teto DURO de futuro da RPC (`fluxo_de_caixa`: passado até 120, futuro até
 * 366). Passar dele não é erro de tela: a função levanta exceção **sem
 * errcode**, então a frase em português sai crua no aviso e não há como
 * distinguir por código. Quem pede janela grande confere aqui antes.
 */
export const TETO_FUTURO = 366;

/**
 * Quantos dias de futuro pedir para cobrir `meses` meses inteiros a partir de
 * hoje — inclusive o mês corrente, até o último dia do último mês.
 *
 * Doze meses dão entre 334 e 365 dias dependendo do dia em que se pergunta
 * (medido em 27/09/2026: 338), então NÃO se escreve um número: calcula-se. E o
 * resultado é limitado ao teto, porque 12 meses cabem e 13 não.
 */
export function diasDeFuturoParaMeses(hojeISO: string, meses: number): number {
  if (!Number.isInteger(meses) || meses < 1) throw new RangeError(`Meses inválido: ${meses}.`);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(hojeISO);
  if (!m) throw new RangeError(`Data fora do formato AAAA-MM-DD: "${hojeISO}".`);
  const hoje = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // Dia 0 do mês seguinte ao último = último dia do último mês.
  const fim = Date.UTC(Number(m[1]), Number(m[2]) - 1 + meses, 0);
  const dias = Math.round((fim - hoje) / 86_400_000);
  return Math.max(0, Math.min(TETO_FUTURO, dias));
}

/** Se o gráfico vai desenhar dia a dia ou agregado por semana. */
export function escalaDosPontos(quantosDias: number): 'dia' | 'semana' {
  return quantosDias <= TETO_DIARIO ? 'dia' : 'semana';
}

export interface PontoDoGrafico {
  /** 'YYYY-MM-DD' do dia (ou da SEGUNDA da semana, quando agregado). */
  dia: string;
  rotulo: string;
  passado: boolean;
  entradas: number;          // realizadas + previstas do período
  saidas: number;
  entradasPrevistas: number; // só a fatia prevista (para o tom apagado)
  saidasPrevistas: number;
  semValor: number;
  acumulado: number;         // o último acumulado do período
}

const rotuloCurto = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

function pontoDeDia(d: FluxoDia): PontoDoGrafico {
  return {
    dia: d.dia,
    rotulo: rotuloCurto(d.dia),
    passado: d.passado,
    entradas: Number(d.entradas_realizadas) + Number(d.entradas_previstas),
    saidas: Number(d.saidas_realizadas) + Number(d.saidas_previstas),
    entradasPrevistas: Number(d.entradas_previstas),
    saidasPrevistas: Number(d.saidas_previstas),
    semValor: d.contas_sem_valor,
    acumulado: Number(d.saldo_acumulado),
  };
}

/** A segunda-feira da semana do dia (data pura, sem fuso — só aritmética). */
export function segundaDe(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const recua = (d.getDay() + 6) % 7;   // dom=6, seg=0, ter=1…
  d.setDate(d.getDate() - recua);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Dias → pontos do gráfico, agregando por semana quando a janela passa do
 * teto. A semana é "passada" só se TODOS os dias dela são passados — a semana
 * corrente mistura realizado e previsto, e pintá-la de sólido mentiria.
 */
export function pontosDoGrafico(dias: FluxoDia[]): PontoDoGrafico[] {
  if (dias.length <= TETO_DIARIO) return dias.map(pontoDeDia);

  const mapa = new Map<string, PontoDoGrafico>();
  for (const d of dias) {
    const chave = segundaDe(d.dia);
    let p = mapa.get(chave);
    if (!p) {
      p = {
        dia: chave, rotulo: rotuloCurto(chave), passado: true,
        entradas: 0, saidas: 0, entradasPrevistas: 0, saidasPrevistas: 0,
        semValor: 0, acumulado: 0,
      };
      mapa.set(chave, p);
    }
    p.passado = p.passado && d.passado;
    p.entradas += Number(d.entradas_realizadas) + Number(d.entradas_previstas);
    p.saidas += Number(d.saidas_realizadas) + Number(d.saidas_previstas);
    p.entradasPrevistas += Number(d.entradas_previstas);
    p.saidasPrevistas += Number(d.saidas_previstas);
    p.semValor += d.contas_sem_valor;
    p.acumulado = Number(d.saldo_acumulado);   // o último do período vence
  }
  return [...mapa.values()].sort((a, b) => a.dia.localeCompare(b.dia));
}

/** "R$ 1,2 mil" — o eixo Y não tem lugar para "R$ 1.234,56". */
export function abreviarBRL(v: number): string {
  const abs = Math.abs(v);
  const sinal = v < 0 ? '−' : '';
  if (abs >= 1_000_000) return `${sinal}R$ ${(abs / 1_000_000).toFixed(1).replace('.', ',')} mi`;
  if (abs >= 1_000) return `${sinal}R$ ${(abs / 1_000).toFixed(1).replace('.', ',')} mil`;
  return `${sinal}R$ ${Math.round(abs)}`;
}

export interface EscalaDoGrafico {
  /** O maior valor absoluto entre barras e linha — o desenho normaliza por ele. */
  teto: number;
  /** Marcas "bonitas" do eixo Y (positivas; o desenho espelha para baixo). */
  ticks: number[];
}

/** Escala que aguenta negativo: o teto olha barras E o acumulado. */
export function escalaDoGrafico(pontos: PontoDoGrafico[]): EscalaDoGrafico {
  const maior = Math.max(
    1,
    ...pontos.map((p) => Math.max(p.entradas, p.saidas, Math.abs(p.acumulado))),
  );
  // Arredonda para uma marca "de gente": 1/2/5 × 10^n.
  const pot = 10 ** Math.floor(Math.log10(maior));
  const norm = maior / pot;
  const passo = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  const teto = passo * pot;
  return { teto, ticks: [teto / 2, teto] };
}

export interface ResumoDaJanela {
  entraPrevisto: number;
  saiPrevisto: number;
  /** O dia (futuro) em que a variação acumulada é mais baixa — o aperto. */
  piorDia: { dia: string; acumulado: number } | null;
  semValor: number;
}

export function resumoDaJanela(dias: FluxoDia[]): ResumoDaJanela {
  let entraPrevisto = 0;
  let saiPrevisto = 0;
  let semValor = 0;
  let piorDia: ResumoDaJanela['piorDia'] = null;
  for (const d of dias) {
    entraPrevisto += Number(d.entradas_previstas);
    saiPrevisto += Number(d.saidas_previstas);
    semValor += d.contas_sem_valor;
    if (!d.passado && (piorDia === null || Number(d.saldo_acumulado) < piorDia.acumulado)) {
      piorDia = { dia: d.dia, acumulado: Number(d.saldo_acumulado) };
    }
  }
  return { entraPrevisto, saiPrevisto, piorDia, semValor };
}
